import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { ShiftsPageView } from './ShiftsPageView';
import { useShiftsPage } from './useShiftsPage';

/**
 * `/branches/shifts` — the shift register, and the combined ledger balance of
 * any shift in it. The second tab of the Branches shell.
 *
 * Sales post **per receipt**, which is what the accountant confirmed and what
 * keeps every amount traceable to the receipt that produced it. The cost is that
 * the journal now carries one entry per sale and *"what did this shift take?"*
 * has no answer on any screen. This page is that answer: a filtered register to
 * find the shift, and a rollup that reads its posted entries back grouped by
 * account — the figures a consolidated entry would have carried, without the
 * system having consolidated anything.
 *
 * It is not in the Operations Activity tab, which is the other place a rollup
 * of posted entries could plausibly live. Activity queries recorded operations
 * and shows no `SALE` rows at all; putting shift rollups there would make one
 * tab answer two unrelated questions.
 *
 * It sits under Branches rather than Sales, where it started. Sales is a
 * dashboard read over a period; this is a register you filter and drill into,
 * and every row of it belongs to a till at a branch — the filter above the
 * table is a branch picker. Beside the branch list, the two tabs are the same
 * subject at two depths: which branches exist, and what happened at one.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner
 * Content child, because React boundaries do not catch errors thrown by the
 * component that renders them.
 *
 * **The boundary is for the register's read.** An unreachable register renders
 * as no rows, which is indistinguishable from a tenant whose tills have never
 * opened a shift — the two must never converge on the same pixels, so a failed
 * read throws and a genuinely empty register gets its own honest empty state. A
 * failed *rollup* read is the exception and renders inside the drawer.
 */
export const ShiftsPage: React.FC = () => {
    const page = useShiftsPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('branches.shifts.error.title'),
            errorMessage: t('branches.shifts.error.message'),
            errorRetry: t('branches.shifts.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('branches-shifts-page')}
        >
            <ErrorBoundary
                data-testid="branches-shifts-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="branches-shifts-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <ShiftsPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type ShiftsPageContentProps = {
    page: ReturnType<typeof useShiftsPage>;
};

const ShiftsPageContent: React.FC<ShiftsPageContentProps> = ({ page }) => {
    if (page.error) {
        throw page.error;
    }

    return (
        <ShiftsPageView
            access={page.access}
            branches={page.branches}
            canRead={page.canRead}
            filters={page.filters}
            isLoading={page.isLoading}
            locale={page.locale}
            onBranchChange={page.onBranchChange}
            onCloseRollup={page.onCloseRollup}
            onPageChange={page.onPageChange}
            onRangeChange={page.onRangeChange}
            onSelect={page.onSelect}
            onStatusChange={page.onStatusChange}
            pageInfo={page.pageInfo}
            reconciliation={page.reconciliation}
            staleCount={page.staleCount}
            rows={page.rows}
            selectedShiftId={page.selectedShiftId}
        />
    );
};
